import { z } from 'zod';
import { ApproachIdSchema, entity, IdSchema, IsoDateSchema, IsoDateTimeSchema } from './common';

/** Amendment A33: 채택한 치료 방향 */
export const TreatmentPlanSchema = z
  .object({
    approach: ApproachIdSchema,
    adoptedAt: IsoDateTimeSchema,
    /** 채택할 때 가장 최근 회기 번호 (이후 근거와 구분) */
    adoptedAtSession: z.number().int().min(0).optional(),
    /** 지금 단계 (0부터, 단계 수와 같으면 모든 단계를 마침) */
    currentStep: z.number().int().min(0).max(50),
    status: z.enum(['active', 'completed']),
    completedAt: IsoDateTimeSchema.optional(),
    /** Amendment A36: 보조 접근 하나 (주 이론의 틀 안에서 정한 자리에만 쓴다) */
    secondary: z
      .object({ approach: ApproachIdSchema, role: z.string().max(300).optional(), addedAt: IsoDateTimeSchema })
      .strict()
      .optional(),
    history: z
      .array(
        z
          .object({ approach: ApproachIdSchema, adoptedAt: IsoDateTimeSchema, endedAt: IsoDateTimeSchema, reason: z.string().max(1000).optional() })
          .strict(),
      )
      .optional(),
  })
  .strict();
export type TreatmentPlan = z.infer<typeof TreatmentPlanSchema>;

export const ClientStatusSchema = z.enum(['active', 'paused', 'terminated']);

export const ClientSchema = entity('Client', {
  clientCode: z.string().min(1).max(32),
  alias: z.string().optional(),
  status: ClientStatusSchema,

  firstSessionDate: IsoDateSchema.optional(),
  totalSessions: z.number().int().min(0),

  ageRange: z.string().optional(),
  birthYear: z.number().int().min(1900).max(2100).optional(),
  gender: z.string().optional(),
  occupationRole: z.string().optional(),
  /** Amendment A25: 사례보고서 인적 사항 (학교·학점, 종교, 알코올 등 물질 관련) */
  education: z.string().optional(),
  religion: z.string().optional(),
  substanceUse: z.string().optional(),
  /** Amendment A25: 최대 상담 가능 회기 수 */
  maxSessions: z.number().int().min(1).max(500).optional(),
  /** Amendment A28: 상담자가 이끌어 가고자 하는 방향 (AI 분석자료에 함께 보냄) */
  counselingDirection: z.string().max(2000).optional(),
  /** Amendment A29: 이 내담자의 외부 AI 프로젝트 주소 (https만). '외부로 보내기'가 바로 이 프로젝트를 연다 */
  externalProjectUrl: z.string().url().startsWith('https://').max(500).optional(),
  /** Amendment A33: 채택한 치료 방향 */
  treatmentPlan: TreatmentPlanSchema.optional(),

  referralSource: z.string().optional(),
  counselingContext: z.string().optional(),
  priorCounselingHistory: z.string().optional(),

  medicalPsychiatricHistory: z.string().optional(),
  currentMedication: z.array(z.string()).optional(),

  familyStructure: z.string().optional(),
  importantRelationships: z.array(z.string()).optional(),
  faithSpiritualContext: z.string().optional(),
  majorLifeEvents: z.array(z.string()).optional(),

  tags: z.array(z.string()).optional(),

  // Amendment A10
  terminationDate: IsoDateSchema.optional(),
  terminationReason: z.enum(['plan_completed', 'client_request', 'referral', 'other']).optional(),
  terminationNote: z.string().optional(),
});
export type Client = z.infer<typeof ClientSchema>;

/** PII는 별도 entity (DATA_SCHEMA §4). AI Packet에 절대 포함하지 않는다. */
export const ClientIdentitySchema = entity('ClientIdentity', {
  clientId: IdSchema,
  realName: z.string().optional(),
  phone: z.string().optional(),
  birthDate: IsoDateSchema.optional(),
  address: z.string().optional(),
  emergencyContact: z.string().optional(),
});
export type ClientIdentity = z.infer<typeof ClientIdentitySchema>;

export const IntakeSummarySchema = entity('IntakeSummary', {
  clientId: IdSchema,

  presentingProblems: z.array(z.string()),
  onset: z.string().optional(),
  duration: z.string().optional(),
  frequency: z.string().optional(),
  severity: z.string().optional(),
  functionalImpact: z.string().optional(),

  referralReason: z.string().optional(),
  clientExpectation: z.string().optional(),
  counselorExpectation: z.string().optional(),

  domains: z
    .object({
      family: z.string().optional(),
      relationship: z.string().optional(),
      schoolWork: z.string().optional(),
      finance: z.string().optional(),
      health: z.string().optional(),
      sleep: z.string().optional(),
      eating: z.string().optional(),
      social: z.string().optional(),
      faithSpirituality: z.string().optional(),
    })
    .strict()
    .optional(),

  strengths: z.array(z.string()).optional(),
  supportResources: z.array(z.string()).optional(),
  copingResources: z.array(z.string()).optional(),

  immediateConcerns: z.array(z.string()).optional(),
  longTermConcerns: z.array(z.string()).optional(),
});
export type IntakeSummary = z.infer<typeof IntakeSummarySchema>;
